import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));

  // Set axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Force-logout banned users on any API 403 with the ban marker header
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 403 &&
          error.response?.headers?.['x-account-banned'] === '1' &&
          localStorage.getItem('authToken')
        ) {
          const detail = error.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : 'Your account has been banned.';
          localStorage.removeItem('authToken');
          localStorage.setItem('banMessage', msg);
          delete axios.defaults.headers.common['Authorization'];
          setCurrentUser(null);
          setToken(null);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Load current user on mount
  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API}/auth/me`);
          setCurrentUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { user, token } = response.data;
      
      setCurrentUser(user);
      setToken(token.access_token);
      localStorage.setItem('authToken', token.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid credentials' 
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, userData);
      const { user, token } = response.data;
      
      setCurrentUser(user);
      setToken(token.access_token);
      localStorage.setItem('authToken', token.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
    } finally {
      setCurrentUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const updateUser = async (updates) => {
    try {
      const response = await axios.put(`${API}/auth/me`, updates);
      setCurrentUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  const value = {
    currentUser,
    loading,
    login,
    signup,
    logout,
    updateUser,
    isOwner: currentUser?.role === 'owner',
    isModerator: currentUser?.role === 'moderator',
    isMember: currentUser?.role === 'member',
    isReviewer: !!currentUser?.reviewerMode,
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
